import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { DatePicker } from '@/components/ui/date-picker';
import { CalendarIcon, Download, FileText, Database } from 'lucide-react';
import { useExportData } from '@/hooks/useExportData';
import type { ExportOptions } from '@/types/export';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExportModal = ({ open, onOpenChange }: ExportModalProps) => {
  const { exportData, isExporting } = useExportData();
  
  const [options, setOptions] = useState<ExportOptions>({
    includeTransactions: true,
    includeCategories: true,
    includeAccounts: true,
    includeGoals: true,
    includeTags: true,
    includeTransfers: true,
    includeCreditCardInvoices: true,
    includeInstallmentPurchases: true,
    includeProfile: true,
    format: 'json'
  });

  const [useDateRange, setUseDateRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleExport = async () => {
    const exportOptions: ExportOptions = {
      ...options,
      dateRange: useDateRange && startDate && endDate ? {
        startDate,
        endDate
      } : undefined
    };

    try {
      await exportData(exportOptions);
      onOpenChange(false);
    } catch (error) {
      console.error('Erro na exportação:', error);
    }
  };

  const dataOptions = [
    {
      key: 'includeTransactions' as keyof ExportOptions,
      label: 'Transações',
      description: 'Todas as receitas e despesas registradas',
      icon: '💰'
    },
    {
      key: 'includeTransfers' as keyof ExportOptions,
      label: 'Transferências',
      description: 'Transferências entre contas',
      icon: '🔄'
    }
  ];

  const allSelected = dataOptions.every(option => options[option.key] as boolean);
  const someSelected = dataOptions.some(option => options[option.key] as boolean);

  const toggleAll = () => {
    const newValue = !allSelected;
    const newOptions = { ...options };
    dataOptions.forEach(option => {
      (newOptions[option.key] as boolean) = newValue;
    });
    setOptions(newOptions);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Exportar Dados do Sistema
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Seleção de dados */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-medium">Dados para Exportar</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleAll}
                className="text-xs"
              >
                {allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {dataOptions.map((option) => (
                <div key={option.key} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                  <Checkbox
                    id={option.key}
                    checked={options[option.key] as boolean}
                    onCheckedChange={(checked) => 
                      setOptions(prev => ({ ...prev, [option.key]: checked }))
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={option.key} className="flex items-center gap-2 cursor-pointer">
                      <span>{option.icon}</span>
                      <span className="font-medium">{option.label}</span>
                    </Label>
                    <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Filtro por data */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="dateRange"
                checked={useDateRange}
                onCheckedChange={(checked) => setUseDateRange(checked === true)}
              />
              <Label htmlFor="dateRange" className="flex items-center gap-2 cursor-pointer">
                <CalendarIcon className="h-4 w-4" />
                Filtrar por período (apenas transações e transferências)
              </Label>
            </div>

            {useDateRange && (
              <div className="grid grid-cols-2 gap-4 ml-6">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Data Inicial</Label>
                  <DatePicker
                    value={startDate}
                    onChange={setStartDate}
                    placeholder="Selecione a data inicial"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">Data Final</Label>
                  <DatePicker
                    value={endDate}
                    onChange={setEndDate}
                    placeholder="Selecione a data final"
                  />
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Formato de exportação */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Formato de Exportação</h3>
            <RadioGroup
              value={options.format}
              onValueChange={(value: 'json' | 'csv' | 'xlsx') => 
                setOptions(prev => ({ ...prev, format: value }))
              }
            >
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="json" id="json" />
                <Label htmlFor="json" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Database className="h-4 w-4" />
                  <div>
                    <div className="font-medium">JSON</div>
                    <div className="text-sm text-gray-500">
                      Formato estruturado, ideal para backup completo e reimportação
                    </div>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="csv" id="csv" />
                <Label htmlFor="csv" className="flex items-center gap-2 cursor-pointer flex-1">
                  <FileText className="h-4 w-4" />
                  <div>
                    <div className="font-medium">CSV</div>
                    <div className="text-sm text-gray-500">
                      Formato de planilha, ideal para análise em Excel ou Google Sheets
                    </div>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="xlsx" id="xlsx" />
                <Label htmlFor="xlsx" className="flex items-center gap-2 cursor-pointer flex-1">
                  <FileText className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Excel (.xlsx)</div>
                    <div className="text-sm text-gray-500">
                      Formato de planilha com múltiplas abas, ideal para análise de dados
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Informações adicionais */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">ℹ️ Informações Importantes</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• O arquivo será baixado automaticamente após a exportação</li>
              <li>• Dados sensíveis como senhas não são incluídos</li>
              <li>• O formato JSON preserva a estrutura completa dos dados</li>
              <li>• O formato CSV organiza os dados em seções separadas</li>
              {useDateRange && (
                <li>• O filtro de data se aplica apenas a transações e transferências</li>
              )}
            </ul>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isExporting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting || !someSelected || (useDateRange && (!startDate || !endDate))}
              className="min-w-[120px]"
            >
              {isExporting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Exportando...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Exportar
                </div>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};